import { useQuery } from "@tanstack/react-query"
import { apiRequest } from "src/services/apiClient"
import { findItemByCode } from "src/services/itemService"
import { listReorderConfigs } from "src/services/reorderService"
import { listItemSuppliers } from "src/services/supplierItemService"
import { queryKeys } from "./queryKeys"
import { CATALOGUE_STALE_TIME } from "src/services/queryClient"
import type { ItemDto } from "src/services/itemService"
import type { ItemSupplierRelationship } from "src/services/supplierItemService"

interface GetItemsResponse {
  status?: string
  data?: ItemDto[]
  items?: ItemDto[]
}

/**
 * Item catalogue for a facility, filtered by one or more item types.
 *
 * `types` is an array (e.g. ["FOOD_INGREDIENT", "NON_FOOD_INGREDIENT"]); it's
 * joined into the query key + URL so the cache is keyed per type-set. The item
 * catalogue changes rarely → 30-min catalogue staleTime.
 *
 * Goes through apiClient (auth/error preserved).
 */
export function useItems(facility: string, types: string[]) {
  const typeParam = types.join(",")
  return useQuery({
    queryKey: queryKeys.items.list(facility, typeParam),
    queryFn: async (): Promise<ItemDto[]> => {
      const response = await apiRequest<GetItemsResponse>(
        `/items?facility=${encodeURIComponent(facility)}&type=${typeParam}`,
      )
      return response.data ?? response.items ?? []
    },
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility && typeParam),
  })
}

/**
 * The entire item catalogue for a facility (no type filter). Used by PO
 * creation where any orderable item may be added. Matches the original
 * `/items?facility=` request. Catalogue staleTime.
 */
export function useAllItems(facility: string) {
  return useQuery({
    queryKey: queryKeys.items.list(facility, "ALL"),
    queryFn: async (): Promise<ItemDto[]> => {
      const response = await apiRequest<GetItemsResponse>(
        `/items?facility=${encodeURIComponent(facility)}`,
      )
      return response.data ?? response.items ?? []
    },
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility),
  })
}

/**
 * A single item by code, with multi-facility + inventory fallback (see
 * findItemByCode). Returns null when not found anywhere.
 */
export function useItemDetail(facility: string, itemCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.items.detail(facility, itemCode ?? ""),
    queryFn: () => findItemByCode(facility, itemCode!),
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility && itemCode),
  })
}

export interface ItemReorderConfig {
  reorderPoint: number
  safetyStock: number
  preferredSupplier?: string
  leadTimeDays?: number
}

/**
 * The replenishment policy (reorder point / safety stock / preferred supplier /
 * lead time) for one item from the reorder-config table, or null when no policy
 * has been set. The table is keyed facility#item; the list API returns a page
 * (100) of the facility's configs, which we filter to this item — fine at POC
 * scale (<100 configs/facility); add pagination if a facility outgrows a page.
 *
 * Caller passes `enabled` so we skip the fetch for recipe/finished-good items
 * (which don't carry a reorder policy).
 */
export function useReorderConfig(
  facility: string,
  itemCode: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.items.reorderConfig(facility, itemCode ?? ""),
    queryFn: async (): Promise<ItemReorderConfig | null> => {
      const res = await listReorderConfigs(facility)
      const match = (res.data ?? []).find((c) => c.itemCode === itemCode)
      if (!match) return null
      return {
        reorderPoint: match.reorderPoint,
        safetyStock: match.safetyStock,
        preferredSupplier: match.preferredSupplier || undefined,
        leadTimeDays: match.leadTimeDays,
      }
    },
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility && itemCode && enabled),
  })
}

/**
 * The SIA supplier-item relationships for an item — the suppliers that carry it
 * with pack/UOM/price/lead-time. `enabled` defers the fetch until the Suppliers
 * tab is shown. Returns an empty array for items with no managed supplier.
 */
export function useItemSuppliers(
  facility: string,
  itemCode: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.items.suppliers(facility, itemCode ?? ""),
    queryFn: async (): Promise<ItemSupplierRelationship[]> => {
      const res = await listItemSuppliers(facility, itemCode!)
      return res.suppliers ?? []
    },
    staleTime: CATALOGUE_STALE_TIME,
    enabled: Boolean(facility && itemCode && enabled),
  })
}

export interface ItemPoHistory {
  poNumber: string
  orderDate: string
  quantity: number
  unitOfMeasure: string
  status: string
  supplierName: string
}

interface PoLineDto {
  itemCode?: string
  quantity?: number
  unitOfMeasure?: string
}

interface PoSummaryDto {
  poNumber?: string
  supplierCode?: string
  supplierName?: string
  orderDate?: string
  status?: string
  lines?: PoLineDto[]
}

/**
 * Purchase-order history for an item: every PO at the facility that contains a
 * line for this item, flattened to one row per matching line. `enabled` lets
 * the caller defer the fetch until the purchasing tab is shown.
 */
export function useItemPoHistory(
  facility: string,
  itemCode: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.items.poHistory(facility, itemCode ?? ""),
    queryFn: async (): Promise<ItemPoHistory[]> => {
      const response = await apiRequest<{ data?: PoSummaryDto[] }>(
        `/po?facility=${encodeURIComponent(facility)}`,
      )
      const rows: ItemPoHistory[] = []
      for (const po of response.data ?? []) {
        for (const line of (po.lines ?? []).filter((l) => l.itemCode === itemCode)) {
          rows.push({
            poNumber: po.poNumber ?? "",
            orderDate: po.orderDate ?? "",
            quantity: line.quantity ?? 0,
            unitOfMeasure: line.unitOfMeasure ?? "",
            status: po.status ?? "OPEN",
            supplierName: po.supplierName || po.supplierCode || "",
          })
        }
      }
      return rows
    },
    enabled: Boolean(facility && itemCode && enabled),
  })
}
